import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import { api, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Available themes. Each must have a matching `html[data-theme="<id>"]` block in index.css
// and be listed in the backend ALLOWED_THEMES set. swatch = [bg, accent, accent-2] preview.
export const THEMES = [
  { id: "dark", label: "Întunecat", swatch: ["#05070D", "#176CFF", "#12DFF3"] },
  { id: "light", label: "Luminos", swatch: ["#F8FAFC", "#176CFF", "#12DFF3"] },
  { id: "purple", label: "Mov", swatch: ["#0D0917", "#8B3CFF", "#D24BFF"] },
  { id: "blue", label: "Albastru", swatch: ["#060B17", "#2563EB", "#38BDF8"] },
  { id: "emerald", label: "Smarald", swatch: ["#06100D", "#059669", "#34D399"] },
];

const DEFAULT_THEME = "dark";
const THEME_IDS = THEMES.map((t) => t.id);
const STORAGE_KEY = "vs_theme";

function normalize(theme) {
  return THEME_IDS.includes(theme) ? theme : DEFAULT_THEME;
}

export function applyTheme(theme) {
  document.documentElement.setAttribute("data-theme", normalize(theme));
}

// Read the boot-time choice (set synchronously in index.html before React mounts).
function initialTheme() {
  try {
    return normalize(localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME);
  } catch {
    return DEFAULT_THEME;
  }
}

const ThemeCtx = createContext(null);

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [theme, setThemeState] = useState(initialTheme);

  // Keep <html> + localStorage in sync with state.
  useEffect(() => {
    applyTheme(theme);
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
  }, [theme]);

  // The server is the source of truth per-user: adopt the saved theme on login.
  useEffect(() => {
    if (user?.theme && THEME_IDS.includes(user.theme)) {
      setThemeState(user.theme);
    }
  }, [user]);

  const setTheme = useCallback((next) => {
    const t = normalize(next);
    setThemeState(t);
    if (getToken()) {
      api.put("/me/preferences", { theme: t }).catch(() => { /* best-effort */ });
    }
  }, []);

  return (
    <ThemeCtx.Provider value={{ theme, setTheme, themes: THEMES }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx);
